import React, {useContext, useEffect, useState} from 'react';
import ResultsPageContainer from '../Containers/ResultsPageContainer';
import {RouteComponentProps, useHistory} from 'react-router-dom';
import appContext from '../Foundations/Contexts/AppContext';
import resultsContext from '../Foundations/Contexts/ResultsContext';
import {ICachedResults, IResultsData} from '../Foundations/Types/IResultsData';
import {IBaseSearchParam} from '../Foundations/Types/ISearchParam';
import {ISearchFilterSet} from '../Foundations/Types/ISearchFilter';
import searchFilterContext from '../Foundations/Contexts/SearchFilterContext';
import {
    decorateResultsWithDistance,
    filterResult,
    getResults,
    getResultSummary,
    sortResultByPrice
} from '../Foundations/Repositories/ResultRepository';
import CircularProgress from '@material-ui/core/CircularProgress';
import MapView from '../Containers/MapView/MapView';
import {
    generateFiltersFromResults,
    limitResultByVehicleType,
    resetFilterSet,
    shouldFilterUpdate,
    updateDynamicFilters,
} from '../Foundations/Utils/SearchFilterUtils';
import APP_PATH from '../Foundations/Constants/AppPath';
import {IDepotInfo} from '../Foundations/Types/IDepot';
import {getAvailableSuppliersFromDepots} from '../Foundations/Utils/SupplierUtils';
import IVehicleType from '../Foundations/Types/IVehicleType';
import {getDepotsAtTheArea} from '../Foundations/Repositories/DepotRepository';
import ResultsPageState from '../Foundations/Enum/ResultsPageState';
import {DEFAULT_SEARCH_RADIUS} from "../Foundations/Constants/SearchRadius";

interface IRouteProps {
    referenceId: string;
}

interface IProps extends RouteComponentProps<IRouteProps> {}

const ResultsRoute: React.FC<IProps> = ({ match }) => {
    const history = useHistory();
    const {env} = useContext(appContext);
    const [originalResults, setOriginalResults] = useState<IResultsData[]>([]);
    const [results, setResults] = useState<IResultsData[]>([]);
    const [pageState, setPageState] = useState<ResultsPageState>(ResultsPageState.FETCHING_RESULTS);
    const [searchParam, setSearchParam] = useState<IBaseSearchParam | null>(null);
    const [activeFilters, setActiveFilters] = useState<ISearchFilterSet | null>(null);
    const [availableFilters, setAvailableFilters] = useState<ISearchFilterSet | null>(null);
    const [isMapViewOpen, setIsMapViewOpen] = useState<boolean>(true);
    const [selectedItem, setSelectedItem] = useState<IResultsData | null>(null);
    const [depotsAtArea, setDepotsAtArea] = useState<IDepotInfo[]>([]);
    const [suppliersAtArea, setSuppliersAtArea] = useState<string[]>([]);
    const [resultsIsLimited, setResultsIsLimited] = useState<boolean>(false);
    const [resultsLimitedBy, setResultsLimitedBy] = useState<IVehicleType | null>(null);
    const [availableVehicleTypes, setAvailableVehicleTypes] = useState<IVehicleType[]>([]);
    const [selectedRadius, setSelectedRadius] = useState<number>(DEFAULT_SEARCH_RADIUS * 1000)

    // Watch for filter change,
    // then pass the filtered originalResults as results
    useEffect(() => {
        if (activeFilters !== null && searchParam !== null) {
            const filteredResults: IResultsData[] = filterResult(activeFilters, originalResults, searchParam);
            if (shouldFilterUpdate(activeFilters) === true) {
                const nonDynamicResults = filterResult(activeFilters, originalResults, searchParam, true);
                const newAvailableFilters = generateFiltersFromResults(nonDynamicResults, searchParam);
                const newActiveFilters = updateDynamicFilters(newAvailableFilters, activeFilters);
                setActiveFilters(newActiveFilters);
            }
            setResults(filteredResults);
        }

        if (activeFilters === null && results.length > 0) {
            setResults(originalResults);
            setAvailableFilters((prevFilters: ISearchFilterSet | null) => {
                return prevFilters !== null ? resetFilterSet(prevFilters) : null;
            });
        }
    }, [originalResults, activeFilters, searchParam]);

    useEffect(() => {
        setPageState(ResultsPageState.FETCHING_RESULTS);
        getResults( env, match.params.referenceId)
            .then((response: ICachedResults) => {
                const {
                    results,
                    isLimited,
                    availableVehicleTypes,
                    limitedBy
                } = limitResultByVehicleType(response.results, response.parameters.vehicleType);
                const searchParam: IBaseSearchParam = response.parameters;

                // Enable all types if current results does not have any selected vehicleType
                if (isLimited === false && searchParam.vehicleType !== 'undefined') {
                    delete searchParam.vehicleType;
                }

                const sortedResults: IResultsData[] = sortResultByPrice(decorateResultsWithDistance(results, response.parameters));
                setActiveFilters(null);
                setResultsIsLimited(isLimited);
                setResultsLimitedBy(limitedBy);
                setAvailableVehicleTypes(availableVehicleTypes);
                setSearchParam(searchParam);
                setResults(sortedResults);
                setOriginalResults(
                    sortedResults.length === 0 && isLimited === true
                        ? sortResultByPrice(decorateResultsWithDistance(response.results, response.parameters))
                        : sortedResults
                );
                setAvailableFilters(generateFiltersFromResults(results, searchParam));
                setPageState(ResultsPageState.FETCHING_DEPOTS);
                getDepotsAtTheArea(env, searchParam)
                    .then((res) => {
                        setDepotsAtArea(res);
                        setSuppliersAtArea(getAvailableSuppliersFromDepots(res));
                        setPageState(ResultsPageState.COMPLETE);
                    });
            })
            .catch(() => {
                history.push(`${APP_PATH}search/${match.params.referenceId}`);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [env, match.params.referenceId, history]);

    const handleVehicleTypeChange = (val: IVehicleType) => {
        if (searchParam !== null) {
            const newSearchParam: IBaseSearchParam = { ...searchParam}
            newSearchParam.vehicleType = val.type;
            const {
                results,
                isLimited,
                availableVehicleTypes,
                limitedBy
            } = limitResultByVehicleType(originalResults, val.type);

            setActiveFilters(null);
            setOriginalResults(results);

            const sortedResults: IResultsData[] = sortResultByPrice(decorateResultsWithDistance(results, searchParam));
            setResultsIsLimited(isLimited);
            setResultsLimitedBy(limitedBy);
            setAvailableVehicleTypes(availableVehicleTypes);
            setResults(sortedResults);
            setSearchParam(newSearchParam);
            setAvailableFilters(generateFiltersFromResults(results, newSearchParam));
        }
    }

    return (
        <resultsContext.Provider
            value={{
                ref: match.params.referenceId,
                results,
                resultSummary: getResultSummary(results, depotsAtArea),
                isMapViewOpen,
                selectedItem,
                setSelectedItem: (newSelectedItem) => setSelectedItem(newSelectedItem),
                setResults: (newResults) => setResults(newResults),
                setMapView: (val: boolean) => setIsMapViewOpen(val),
                suppliersAtArea,
                depotsAtArea,
                isLimited: resultsIsLimited,
                limitedBy: resultsLimitedBy,
                availableVehicleTypes,
                setLimitedBy: handleVehicleTypeChange,
                selectedRadius,
                setSelectedRadius
            }}>
            <searchFilterContext.Provider
                value={{
                    activeFilters: activeFilters,
                    availableFilters: availableFilters,
                    setActiveFilters: (filters: ISearchFilterSet | null) => setActiveFilters(filters),
                    searchParameters: searchParam
                }}
            >
                <div className="bg-gray-100 text-sm font-main flex">
                    <div className={`max-w-6xl h-full mx-auto w-full`}>
                        {pageState !== ResultsPageState.COMPLETE
                            ? (
                                <div className="flex h-screen justify-center items-center">
                                    <div className="text-center max-w-300">
                                        <CircularProgress/>
                                        <span className="block text-black-400 mt-5 opacity-60">
                                            {pageState}...
                                        </span>
                                    </div>
                                </div>
                            )
                            : <ResultsPageContainer/>
                        }
                    </div>
                    {
                        isMapViewOpen === true &&
                        searchParam !== null &&
                        <MapView />
                    }
                </div>
            </searchFilterContext.Provider>
        </resultsContext.Provider>
    )
}

export default ResultsRoute;
